/**
 * @group Network
 * @summary HTTP status names for network errors
 * 
 * @remarks
 * This constant contains standard HTTP status names used in API responses
 * for consistent error handling and success responses.
 * 
 * @example
 * ```typescript
 * import { HTTP_STATUS_NAMES } from '@shared/constants';
 * 
 * if (!user) {
 *   return res.status(HTTP_STATUS_NAMES.NOT_FOUND).json({ error: 'User not found' });
 * }
 * 
 * return res.status(HTTP_STATUS_NAMES.OK).json({ data: user });
 * ```
 */
export const HTTP_STATUS_NAMES = {
    // 1xx Informational
    CONTINUE: "CONTINUE",
    SWITCHING_PROTOCOLS: "SWITCHING_PROTOCOLS",
    PROCESSING: "PROCESSING",
    EARLY_HINTS: "EARLY_HINTS",
  
    // 2xx Success
    OK: "OK",
    CREATED: "CREATED",
    ACCEPTED: "ACCEPTED",
    NON_AUTHORITATIVE_INFORMATION: "NON_AUTHORITATIVE_INFORMATION",
    NO_CONTENT: "NO_CONTENT",
    RESET_CONTENT: "RESET_CONTENT",
    PARTIAL_CONTENT: "PARTIAL_CONTENT",
    MULTI_STATUS: "MULTI_STATUS",
    ALREADY_REPORTED: "ALREADY_REPORTED",
    IM_USED: "IM_USED",
  
    // 3xx Redirection
    MULTIPLE_CHOICES: "MULTIPLE_CHOICES",
    MOVED_PERMANENTLY: "MOVED_PERMANENTLY",
    FOUND: "FOUND",
    SEE_OTHER: "SEE_OTHER",
    NOT_MODIFIED: "NOT_MODIFIED",
    USE_PROXY: "USE_PROXY",
    TEMPORARY_REDIRECT: "TEMPORARY_REDIRECT",
    PERMANENT_REDIRECT: "PERMANENT_REDIRECT",
  
    // 4xx Client Errors
    BAD_REQUEST: "BAD_REQUEST",
    UNAUTHORIZED: "UNAUTHORIZED",
    PAYMENT_REQUIRED: "PAYMENT_REQUIRED",
    FORBIDDEN: "FORBIDDEN",
    NOT_FOUND: "NOT_FOUND",
    METHOD_NOT_ALLOWED: "METHOD_NOT_ALLOWED",
    NOT_ACCEPTABLE: "NOT_ACCEPTABLE",
    PROXY_AUTHENTICATION_REQUIRED: "PROXY_AUTHENTICATION_REQUIRED",
    REQUEST_TIMEOUT: "REQUEST_TIMEOUT",
    CONFLICT: "CONFLICT",
    GONE: "GONE",
    LENGTH_REQUIRED: "LENGTH_REQUIRED",
    PRECONDITION_FAILED: "PRECONDITION_FAILED",
    PAYLOAD_TOO_LARGE: "PAYLOAD_TOO_LARGE",
    URI_TOO_LONG: "URI_TOO_LONG",
    UNSUPPORTED_MEDIA_TYPE: "UNSUPPORTED_MEDIA_TYPE",
    RANGE_NOT_SATISFIABLE: "RANGE_NOT_SATISFIABLE",
    EXPECTATION_FAILED: "EXPECTATION_FAILED",
    IM_A_TEAPOT: "IM_A_TEAPOT",
    MISDIRECTED_REQUEST: "MISDIRECTED_REQUEST",
    UNPROCESSABLE_ENTITY: "UNPROCESSABLE_ENTITY",
    LOCKED: "LOCKED",
    FAILED_DEPENDENCY: "FAILED_DEPENDENCY",
    TOO_EARLY: "TOO_EARLY",
    UPGRADE_REQUIRED: "UPGRADE_REQUIRED",
    PRECONDITION_REQUIRED: "PRECONDITION_REQUIRED",
    TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
    REQUEST_HEADER_FIELDS_TOO_LARGE: "REQUEST_HEADER_FIELDS_TOO_LARGE",
    UNAVAILABLE_FOR_LEGAL_REASONS: "UNAVAILABLE_FOR_LEGAL_REASONS",
  
    // 5xx Server Errors
    INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR",
    NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
    BAD_GATEWAY: "BAD_GATEWAY",
    SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
    GATEWAY_TIMEOUT: "GATEWAY_TIMEOUT",
    HTTP_VERSION_NOT_SUPPORTED: "HTTP_VERSION_NOT_SUPPORTED",
    VARIANT_ALSO_NEGOTIATES: "VARIANT_ALSO_NEGOTIATES",
    INSUFFICIENT_STORAGE: "INSUFFICIENT_STORAGE",
    LOOP_DETECTED: "LOOP_DETECTED",
    NOT_EXTENDED: "NOT_EXTENDED",
    NETWORK_AUTHENTICATION_REQUIRED: "NETWORK_AUTHENTICATION_REQUIRED",
  } as const;